import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useWatch } from "react-hook-form";
import { toast } from "react-toastify";
import type { DocumentDetails } from "~/api/types";
import { updateDocument, type DocumentEditableField } from "~/api/v2/requests";
import { Button } from "~/atoms/button";
import { DialogFooter } from "~/atoms/dialog";
import { Form } from "~/atoms/form";
import { CenterSpinner } from "~/atoms/spinner";
import { useDocumentEditForm } from "~/hooks/useDocumentEditForm";
import { useDocumentLinking } from "~/hooks/useDocumentLinking";
import { TagForm } from "~/molecules/TagForm";
import { DocumentCategoryField } from "~/molecules/document-fields/DocumentCategoryField";
import { DocumentLinkField } from "~/molecules/document-fields/DocumentLinkField";
import { DocumentManagersFields } from "~/molecules/document-fields/DocumentManagersFields";
import { DocumentReferenceNumberField } from "~/molecules/document-fields/DocumentReferenceNumberField";
import { DocumentScheduleReviewPeriodField } from "~/molecules/document-fields/DocumentScheduledReviewFields";
import { DocumentTierField } from "~/molecules/document-fields/DocumentTierField";
import { getPayloadForField } from "~/helpers/libraryEditing";

interface DocumentFieldEditFormProps {
  document: DocumentDetails;
  field: DocumentEditableField;
  onCancel: () => void;
  onSaved: () => Promise<void>;
}

export const DocumentFieldEditForm = ({
  document,
  field,
  onCancel,
  onSaved,
}: DocumentFieldEditFormProps) => {
  const { t } = useTranslation();
  const { form, filterUnchangedValues } = useDocumentEditForm({
    document,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedTags, selectedLinkedDocuments] = useWatch({
    control: form.control,
    name: ["tags", "linkedDocuments"],
  });
  const { contexts, isLoading: isLinkContextLoading } = useDocumentLinking({
    document,
    selectedLinkedDocuments,
  });

  const handleSubmit = form.handleSubmit(
    async (values) => {
      const payload = getPayloadForField(field, filterUnchangedValues(values));
      if (Object.keys(payload).length === 0) {
        onCancel();
        return;
      }

      try {
        setIsSubmitting(true);
        await updateDocument(document.id, payload);
        toast.success(t(($) => $.toasts.success.documentUpdated));
        await onSaved();
        onCancel();
      } catch {
        toast.error(t(($) => $.toasts.error.generic));
      } finally {
        setIsSubmitting(false);
      }
    },
    (errors) => {
      const firstError = Object.values(errors).find((error) => error?.message);
      if (firstError?.message) {
        toast.error(firstError.message);
      } else {
        toast.error(t(($) => $.toasts.error.generic));
      }
    },
  );

  return (
    <Form {...form}>
      <form onSubmit={handleSubmit} className="flex flex-col gap-4 pt-2">
        {field === "categoryId" && <DocumentCategoryField />}
        {field === "tierId" && <DocumentTierField />}
        {field === "referenceNumber" && <DocumentReferenceNumberField />}
        {field === "scheduleReviewPeriod" && (
          <DocumentScheduleReviewPeriodField />
        )}
        {field === "ownerId" && <DocumentManagersFields mode="ownerId" />}
        {field === "publisherId" && (
          <DocumentManagersFields mode="publisherId" />
        )}
        {field === "tags" && (
          <TagForm
            selectedTags={selectedTags}
            onTagsChange={(tags: string[]) => form.setValue("tags", tags)}
          />
        )}
        {field === "links" &&
          (isLinkContextLoading ? (
            <CenterSpinner />
          ) : contexts.length === 0 ? (
            <p className="text-muted-foreground text-sm">
              {t(($) => $.commons.document.noLinkingOptions)}
            </p>
          ) : (
            <div className="flex flex-col gap-4">
              {contexts.map((context) => (
                <DocumentLinkField
                  key={context.key}
                  title={context.title}
                  selectedDocuments={context.selectedDocuments}
                  options={context.options}
                />
              ))}
            </div>
          ))}
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onCancel}>
            {t(($) => $.commons.buttons.cancel)}
          </Button>
          <Button type="submit" disabled={isSubmitting}>
            {t(($) => $.commons.buttons.save)}
          </Button>
        </DialogFooter>
      </form>
    </Form>
  );
};
